import { useEffect, useState } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { Card } from '@/components/ui/card';
import { CheckCircle2, XCircle, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const WhatsAppConnection = () => {
  const [status, setStatus] = useState({ connected: false, phone_number: null });
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    checkStatus();
    const interval = setInterval(() => {
      checkStatus();
      if (!status.connected) {
        fetchQR();
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [status.connected]);

  const checkStatus = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/whatsapp/status`);
      setStatus(response.data);
      if (response.data.connected) {
        setQrCode(null);
      }
    } catch (error) {
      console.error('Failed to check status:', error);
    }
  };

  const fetchQR = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/whatsapp/qr`);
      if (response.data.qr) {
        setQrCode(response.data.qr);
      }
    } catch (error) {
      console.error('Failed to fetch QR:', error);
    }
  };

  const syncGroups = async () => {
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/whatsapp/sync-groups`);
      toast.success('Groups synced successfully!');
    } catch (error) {
      toast.error('Failed to sync groups');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="whatsapp-connection-page" className="space-y-8">
      <div>
        <h1 className="text-4xl md:text-5xl font-heading font-bold text-foreground tracking-tight">WHATSAPP CONNECTION</h1>
        <p className="text-muted-foreground mt-2 font-body">Connect your WhatsApp to monitor groups</p>
      </div>

      <div className="max-w-2xl mx-auto">
        <Card className="bg-card border border-border p-8">
          <div className="flex items-center justify-center mb-6">
            {status.connected ? (
              <div className="flex items-center gap-3 text-green-500">
                <CheckCircle2 className="w-8 h-8" strokeWidth={2} />
                <div>
                  <h2 className="text-2xl font-heading font-bold">Connected</h2>
                  {status.phone_number && (
                    <p className="text-sm text-muted-foreground">Phone: +{status.phone_number}</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 text-yellow-500">
                <XCircle className="w-8 h-8" strokeWidth={2} />
                <h2 className="text-2xl font-heading font-bold">Not Connected</h2>
              </div>
            )}
          </div>

          {!status.connected && qrCode && (
            <div className="space-y-6">
              <div className="bg-white p-6 rounded-md w-fit mx-auto">
                <QRCodeSVG value={qrCode} size={256} level="H" data-testid="qr-code" />
              </div>
              <div className="text-center">
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">Scan with WhatsApp</h3>
                <ol className="text-sm text-muted-foreground space-y-1 text-left max-w-md mx-auto">
                  <li>1. Open WhatsApp on your phone</li>
                  <li>2. Tap Menu (⋮) or Settings</li>
                  <li>3. Tap Linked Devices</li>
                  <li>4. Tap Link a Device</li>
                  <li>5. Point your phone at this screen to scan the code</li>
                </ol>
              </div>
            </div>
          )}

          {!status.connected && !qrCode && (
            <div className="text-center py-8">
              <Loader2 className="w-12 h-12 text-primary mx-auto animate-spin" />
              <p className="text-muted-foreground mt-4">Generating QR code...</p>
            </div>
          )}

          {status.connected && (
            <div className="space-y-4">
              <p className="text-center text-muted-foreground">
                Your WhatsApp is connected and monitoring groups.
              </p>
              <button
                data-testid="sync-groups-btn"
                onClick={syncGroups}
                disabled={loading}
                className="w-full bg-primary text-primary-foreground px-6 py-3 rounded-md font-body font-medium hover:brightness-110 active:scale-95 transition-transform duration-200 flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <RefreshCw className="w-5 h-5" />
                )}
                Sync Groups Now
              </button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default WhatsAppConnection;
